import { useCallback, useEffect, useState } from 'react';
import { admin_api } from '@/lib/api';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, CalendarBlank } from '@phosphor-icons/react';

const VIEWS = { day: 1, week: 7, month: 30 };
const STATUS_DOT = {
  pending: 'bg-neutral-400', confirmed: 'bg-blue-500', assigned: 'bg-indigo-500',
  en_route: 'bg-blue-600', arrived: 'bg-cyan-500', in_progress: 'bg-amber-500',
  completed: 'bg-green-500', no_show: 'bg-red-500',
};

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function AdminSchedule() {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(new Date());
  const [drivers, setDrivers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [loading, setLoading] = useState(true);

  const days = Array.from({ length: VIEWS[view] }, (_, i) => iso(addDays(anchor, i)));
  const start = days[0];
  const end = days[days.length - 1];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([admin_api.schedule(start, end), admin_api.driversList()]);
      setBookings(s.items || []);
      setDrivers((d.items || []).filter(x => x.status !== 'inactive'));
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const rows = [{ id: null, full_name: 'Unassigned' }, ...drivers];
  const cell = (driverId, date) => bookings.filter(b => (b.assigned_driver_id || null) === driverId && b.pickup_date === date);

  const drop = async (driverId) => {
    if (!dragged) return;
    const b = dragged;
    setDragged(null);
    if ((b.assigned_driver_id || null) === driverId) return;
    const doAssign = async (force) => admin_api.assignBooking(b.id, {
      driver_id: driverId, vehicle_id: b.assigned_vehicle_id || null, force,
    });
    try {
      await doAssign(false);
      toast.success(driverId ? 'Booking reassigned.' : 'Booking unassigned.');
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.conflicts) {
        const list = detail.conflicts.map(c => `${c.full_name} @ ${c.pickup_time}`).join(', ');
        if (window.confirm(`Conflict with: ${list}. Reassign anyway?`)) {
          await doAssign(true); toast.success('Reassigned (conflict overridden).'); load();
        }
      } else {
        toast.error('Could not reassign.');
      }
    }
  };

  const shift = (dir) => setAnchor(a => addDays(a, dir * VIEWS[view]));

  return (
    <div data-testid="admin-schedule">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Dispatch</div>
          <h1 className="font-display font-extrabold text-3xl text-ink">Schedule board</h1>
          <p className="text-ink-muted text-sm mt-1">Drag a booking onto a driver row to reassign it. Conflicts ask before overriding.</p>
        </div>
        <div className="flex items-center gap-2">
          {Object.keys(VIEWS).map(v => (
            <button key={v} onClick={() => setView(v)} data-testid={`schedule-view-${v}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold border transition ${view === v ? 'bg-ink text-white border-ink' : 'border-neutral-300 text-ink-soft hover:border-brand'}`}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
          <button onClick={() => shift(-1)} data-testid="schedule-prev" className="p-2 rounded-full border border-neutral-300 hover:border-brand"><CaretLeft size={14} /></button>
          <button onClick={() => setAnchor(new Date())} data-testid="schedule-today" className="px-3 py-2 rounded-full border border-neutral-300 text-xs font-semibold hover:border-brand">Today</button>
          <button onClick={() => shift(1)} data-testid="schedule-next" className="p-2 rounded-full border border-neutral-300 hover:border-brand"><CaretRight size={14} /></button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
        <table className="w-full text-sm" style={{ minWidth: view === 'day' ? 500 : 900 }}>
          <thead>
            <tr className="bg-neutral-50">
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-widest text-ink-muted w-44 sticky left-0 bg-neutral-50">Driver</th>
              {days.map(d => (
                <th key={d} className="text-left px-3 py-3 font-semibold text-xs text-ink-muted whitespace-nowrap">
                  {new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: view === 'month' ? undefined : 'short' })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id || 'unassigned'} className="border-t border-neutral-100"
                onDragOver={e => e.preventDefault()}
                onDrop={() => drop(r.id)}
                data-testid={`schedule-row-${r.id || 'unassigned'}`}>
                <td className={`px-4 py-3 font-semibold sticky left-0 bg-white ${r.id ? 'text-ink' : 'text-ink-muted italic'}`}>
                  {r.full_name}
                  {r.id && r.status === 'on_shift' && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-green-500" title="On shift" />}
                </td>
                {days.map(d => (
                  <td key={d} className="px-2 py-2 align-top">
                    <div className="space-y-1 min-h-[28px]">
                      {cell(r.id, d).map(b => (
                        <div key={b.id} draggable
                          onDragStart={() => setDragged(b)}
                          data-testid={`schedule-chip-${b.id}`}
                          title={`${b.full_name} · ${b.pickup_address} → ${b.dropoff_address}`}
                          className="flex items-center gap-1.5 rounded-lg bg-neutral-50 border border-neutral-200 px-2 py-1 text-[11px] cursor-grab active:cursor-grabbing hover:border-brand transition">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[b.status] || 'bg-neutral-400'}`} />
                          <span className="font-semibold">{b.pickup_time}</span>
                          <span className="truncate max-w-[110px]">{b.full_name}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {loading && <div className="text-ink-muted text-sm mt-4 flex items-center gap-2"><CalendarBlank size={16} /> Loading schedule…</div>}
      {!loading && bookings.length === 0 && (
        <div className="text-ink-muted text-sm mt-4" data-testid="schedule-empty">No bookings in this period.</div>
      )}
    </div>
  );
}
